import { useEffect, useRef, useState } from "react";

interface UseInViewOptions {
  /**
   * The root element to use as the viewport.
   * Default is the browser viewport
   */
  root?: Element | null;
  /**
   * Margin around the root element
   * Default is '0px'
   */
  rootMargin?: string;
  /**
   * Number between 0 and 1 indicating the percentage
   * of the target's visibility needed to trigger callback
   * Default is 0
   */
  threshold?: number | number[];
}

/**
 * Hook to detect when an element enters the viewport
 * @param options IntersectionObserver options
 * @returns [ref, isInView] - Ref to attach to target element and boolean indicating if element is in view
 */
export function useInView<T extends Element = Element>({
  root = null,
  rootMargin = "0px",
  threshold = 0,
}: UseInViewOptions = {}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { root, rootMargin, threshold },
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [root, rootMargin, threshold]);

  return [ref, isInView] as const;
}
